import React from 'react';
import ColorPicker from '../LoaderGenerator/ColorPicker';

const GradientControls = ({ config, onChange }) => {
  const handleChange = (key, value) => {
    onChange({ [key]: value });
  };

  const addColor = () => {
    const newColors = [...config.colors, '#ffffff'];
    handleChange('colors', newColors);
    
    // Also update stops if using stops
    if (config.useStops) {
      const newStops = [...(config.stops || [])];
      // Add new stop at the end (location 1.0) or distribute evenly
      const lastLocation = newStops.length > 0 ? newStops[newStops.length - 1].location : 1.0;
      newStops.push({ color: '#ffffff', location: lastLocation });
      handleChange('stops', newStops);
    }
  };

  const removeColor = (index) => {
    if (config.colors.length > 2) {
      const newColors = config.colors.filter((_, i) => i !== index);
      handleChange('colors', newColors);
      
      // Also update stops if using stops
      if (config.useStops && config.stops) {
        const newStops = config.stops.filter((_, i) => i !== index);
        handleChange('stops', newStops);
      }
    }
  };

  const updateColor = (index, color) => {
    const newColors = [...config.colors];
    newColors[index] = color;
    handleChange('colors', newColors);
    
    // Also update stops if using stops
    if (config.useStops && config.stops && config.stops[index]) {
      const newStops = [...config.stops];
      newStops[index] = { ...newStops[index], color };
      handleChange('stops', newStops);
    }
  };

  const updateStopLocation = (index, location) => {
    if (!config.stops || !config.stops[index]) return;
    const newStops = [...config.stops];
    // Clamp location between 0 and 1
    const clampedLocation = Math.max(0, Math.min(1, parseFloat(location)));
    newStops[index] = { ...newStops[index], location: clampedLocation };
    
    // Sort stops by location to maintain order
    newStops.sort((a, b) => a.location - b.location);
    handleChange('stops', newStops);
    
    // Update colors array to match sorted stops
    const newColors = newStops.map(s => s.color);
    handleChange('colors', newColors);
  };

  const moveColor = (index, direction) => {
    const newColors = [...config.colors];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newColors.length) {
      [newColors[index], newColors[newIndex]] = [newColors[newIndex], newColors[index]];
      handleChange('colors', newColors);
      
      // Also update stops if using stops
      if (config.useStops && config.stops) {
        const newStops = [...config.stops];
        [newStops[index], newStops[newIndex]] = [newStops[newIndex], newStops[index]];
        handleChange('stops', newStops);
      }
    }
  };

  const initializeStops = () => {
    if (!config.stops || config.stops.length === 0) {
      const stops = config.colors.map((color, index) => ({
        color,
        location: index / Math.max(1, config.colors.length - 1)
      }));
      handleChange('stops', stops);
    }
  };

  const handleUseStopsToggle = (enabled) => {
    if (enabled) {
      initializeStops();
    }
    handleChange('useStops', enabled);
  };

  return (
    <div className="space-y-6">
      {/* Mode Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Gradient Mode
        </label>
        <div className="grid grid-cols-2 gap-2">
          {['linear', 'radial', 'angular', 'elliptical'].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => handleChange('mode', mode)}
              className={`
                w-full px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                ${
                  config.mode === mode
                    ? 'bg-gradient-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }
              `}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Direction (for linear) */}
      {config.mode === 'linear' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Direction
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-2 mb-3">
            {[
              { value: 'vertical', label: 'Vertical' },
              { value: 'horizontal', label: 'Horizontal' },
              { value: 'diagonalTL', label: '↖ Top-Left' },
              { value: 'diagonalTR', label: '↗ Top-Right' },
              { value: 'diagonalBL', label: '↙ Bottom-Left' },
              { value: 'diagonalBR', label: '↘ Bottom-Right' },
              { value: 'topToBottom', label: 'Top ↓ Bottom' },
              { value: 'leftToRight', label: 'Left → Right' },
              { value: 'custom', label: 'Custom Angle' },
            ].map((dir) => (
              <button
                key={dir.value}
                type="button"
                onClick={() => handleChange('direction', dir.value)}
                className={`
                  px-3 py-2 rounded-lg text-xs font-medium transition-all
                  w-full flex items-center justify-center text-center whitespace-nowrap
                  ${
                    config.direction === dir.value
                      ? 'bg-gradient-primary text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
                title={dir.value === 'diagonalTL' ? 'Top-Left Diagonal' :
                       dir.value === 'diagonalTR' ? 'Top-Right Diagonal' :
                       dir.value === 'diagonalBL' ? 'Bottom-Left Diagonal' :
                       dir.value === 'diagonalBR' ? 'Bottom-Right Diagonal' :
                       dir.value === 'topToBottom' ? 'Top to Bottom' :
                       dir.value === 'leftToRight' ? 'Left to Right' :
                       dir.value === 'custom' ? 'Custom Angle' :
                       dir.label}
              >
                {dir.label}
              </button>
            ))}
          </div>
          {config.direction === 'custom' && (
            <div className="mt-2">
              <label className="block text-xs text-gray-400 mb-1">
                Angle: {config.customAngle || 0}°
              </label>
              <input
                type="range"
                min="0"
                max="360"
                value={config.customAngle || 0}
                onChange={(e) => handleChange('customAngle', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          )}
        </div>
      )}

      {/* Enhanced Radial/Elliptical Controls */}
      {(config.mode === 'radial' || config.mode === 'elliptical') && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Center Position
            </label>
            <select
              value={config.radialCenter || 'center'}
              onChange={(e) => handleChange('radialCenter', e.target.value)}
              className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border border-gray-600 focus:outline-none focus:border-primary-500"
            >
              <option value="center">Center</option>
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
              <option value="leading">Leading (Left)</option>
              <option value="trailing">Trailing (Right)</option>
              <option value="topLeading">Top Leading</option>
              <option value="topTrailing">Top Trailing</option>
              <option value="bottomLeading">Bottom Leading</option>
              <option value="bottomTrailing">Bottom Trailing</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {(config.radialCenter === 'custom') && (
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  X: {((config.radialCenterX || 0.5) * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={config.radialCenterX || 0.5}
                  onChange={(e) => handleChange('radialCenterX', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Y: {((config.radialCenterY || 0.5) * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={config.radialCenterY || 0.5}
                  onChange={(e) => handleChange('radialCenterY', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Radius: {config.startRadius || 0}
            </label>
            <input
              type="range"
              min="0"
              max="200"
              value={config.startRadius || 0}
              onChange={(e) => handleChange('startRadius', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Radius: {config.endRadius || 100}
            </label>
            <input
              type="range"
              min="0"
              max="500"
              value={config.endRadius || 100}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value > (config.startRadius || 0)) {
                  handleChange('endRadius', value);
                }
              }}
              className="w-full"
            />
            {config.endRadius <= (config.startRadius || 0) && (
              <p className="text-xs text-red-500 mt-1">End radius must be greater than start radius</p>
            )}
          </div>
        </>
      )}

      {/* Enhanced Angular Controls */}
      {config.mode === 'angular' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Center Position
            </label>
            <select
              value={config.angularCenter || 'center'}
              onChange={(e) => handleChange('angularCenter', e.target.value)}
              className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border border-gray-600 focus:outline-none focus:border-primary-500"
            >
              <option value="center">Center</option>
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
              <option value="leading">Leading (Left)</option>
              <option value="trailing">Trailing (Right)</option>
              <option value="topLeading">Top Leading</option>
              <option value="topTrailing">Top Trailing</option>
              <option value="bottomLeading">Bottom Leading</option>
              <option value="bottomTrailing">Bottom Trailing</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {(config.angularCenter === 'custom') && (
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  X: {((config.angularCenterX || 0.5) * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={config.angularCenterX || 0.5}
                  onChange={(e) => handleChange('angularCenterX', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Y: {((config.angularCenterY || 0.5) * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={config.angularCenterY || 0.5}
                  onChange={(e) => handleChange('angularCenterY', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Angle: {config.startAngle || 0}°
            </label>
            <input
              type="range"
              min="0"
              max="360"
              value={config.startAngle || 0}
              onChange={(e) => handleChange('startAngle', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Angle: {config.endAngle !== undefined ? config.endAngle : 360}°
            </label>
            <input
              type="range"
              min="0"
              max="360"
              value={config.endAngle !== undefined ? config.endAngle : 360}
              onChange={(e) => handleChange('endAngle', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Angle (Legacy): {config.angle || 0}°
            </label>
            <input
              type="range"
              min="0"
              max="360"
              value={config.angle || 0}
              onChange={(e) => handleChange('angle', parseInt(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-gray-400 mt-1">Used when start/end angles are 0/360</p>
          </div>
        </>
      )}

      {/* Gradient Stops Toggle */}
      <div>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={config.useStops || false}
            onChange={(e) => handleUseStopsToggle(e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Use Color Stops (Custom Locations)
          </span>
        </label>
        <p className="text-xs text-gray-400 mt-1">
          Enable to control exact color stop positions (0.0 - 1.0)
        </p>
      </div>

      {/* Colors */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Colors ({config.colors.length})
          </label>
          <button
            type="button"
            onClick={addColor}
            className="px-3 py-1 text-xs bg-gradient-primary bg-gradient-primary-hover rounded text-white"
          >
            + Add Color
          </button>
        </div>
        <div className="space-y-4">
          {config.colors.map((color, index) => {
            const stop = config.useStops && config.stops && config.stops[index] 
              ? config.stops[index] 
              : null;
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-start gap-2 flex-wrap">
                  <div className="flex-1 min-w-0 position-relative">
                    <ColorPicker
                      color={color}
                      onChange={(newColor) => updateColor(index, newColor)}
                      label={`Color ${index + 1}`}
                    />
                    {config.useStops && stop && (
                      <div className="mt-2">
                        <label className="block text-xs text-gray-400 mb-1">
                          Location: {stop.location.toFixed(2)}
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={stop.location}
                          onChange={(e) => updateStopLocation(index, e.target.value)}
                          className="w-full"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0" style={{ paddingTop: '28px' }}>
                    <button
                      type="button"
                      onClick={() => moveColor(index, 'up')}
                      disabled={index === 0}
                      className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveColor(index, 'down')}
                      disabled={index === config.colors.length - 1}
                      className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>
                  {config.colors.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeColor(index)}
                      className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 rounded text-white flex-shrink-0 self-start cross-btn"
                      title="Remove color"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Gradient Opacity */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Gradient Opacity: {((config.gradientOpacity !== undefined ? config.gradientOpacity : 1.0) * 100).toFixed(0)}%
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={config.gradientOpacity !== undefined ? config.gradientOpacity : 1.0}
          onChange={(e) => handleChange('gradientOpacity', parseFloat(e.target.value))}
          className="w-full"
        />
      </div>

      {/* Gradient Presets */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Preset Gradients
        </label>
        <select
          value={config.selectedPreset || ''}
          onChange={(e) => {
            const preset = e.target.value;
            if (preset) {
              handleChange('selectedPreset', preset);
              const presets = {
                sunset: { colors: ['#FF6B6B', '#FF8E53', '#FF6B9D', '#C44569'], stops: [
                  {color: '#FF6B6B', location: 0.0},
                  {color: '#FF8E53', location: 0.4},
                  {color: '#FF6B9D', location: 0.7},
                  {color: '#C44569', location: 1.0}
                ]},
                ocean: { colors: ['#4A90E2', '#357ABD', '#50C9CE'], stops: [
                  {color: '#4A90E2', location: 0.0},
                  {color: '#357ABD', location: 0.5},
                  {color: '#50C9CE', location: 1.0}
                ]},
                forest: { colors: ['#2ECC71', '#27AE60', '#16A085'], stops: [
                  {color: '#2ECC71', location: 0.0},
                  {color: '#27AE60', location: 0.5},
                  {color: '#16A085', location: 1.0}
                ]},
                fire: { colors: ['#FF0000', '#FF4500', '#FFA500', '#FFFF00'], stops: [
                  {color: '#FF0000', location: 0.0},
                  {color: '#FF4500', location: 0.33},
                  {color: '#FFA500', location: 0.66},
                  {color: '#FFFF00', location: 1.0}
                ]},
                cool: { colors: ['#667EEA', '#764BA2'], stops: [
                  {color: '#667EEA', location: 0.0},
                  {color: '#764BA2', location: 1.0}
                ]},
                warm: { colors: ['#F093FB', '#F5576C'], stops: [
                  {color: '#F093FB', location: 0.0},
                  {color: '#F5576C', location: 1.0}
                ]},
                iosBlue: { colors: ['#007AFF', '#0051D5'], stops: [
                  {color: '#007AFF', location: 0.0},
                  {color: '#0051D5', location: 1.0}
                ]},
                iosPurple: { colors: ['#AF52DE', '#5856D6'], stops: [
                  {color: '#AF52DE', location: 0.0},
                  {color: '#5856D6', location: 1.0}
                ]},
              };
              if (presets[preset]) {
                onChange({
                  colors: presets[preset].colors,
                  stops: presets[preset].stops,
                  selectedPreset: preset,
                });
              }
            } else {
              handleChange('selectedPreset', null);
            }
          }}
          className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border border-gray-600 focus:outline-none focus:border-primary-500"
        >
          <option value="">None (Custom)</option>
          <option value="sunset">Sunset (Orange → Pink → Purple)</option>
          <option value="ocean">Ocean (Blue → Cyan)</option>
          <option value="forest">Forest (Green → Teal)</option>
          <option value="fire">Fire (Red → Orange → Yellow)</option>
          <option value="cool">Cool (Blue → Purple)</option>
          <option value="warm">Warm (Pink → Red)</option>
          <option value="iosBlue">iOS Blue</option>
          <option value="iosPurple">iOS Purple</option>
        </select>
      </div>

      {/* Component Type Toggle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Component Type
        </label>
        <div className="grid grid-cols-2 gap-2">
          {['view', 'button'].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleChange('componentType', type)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${
                  config.componentType === type
                    ? 'bg-gradient-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }
              `}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Shape Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Shape
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { value: 'rectangle', label: 'Rectangle' },
            { value: 'roundedRectangle', label: 'Rounded' },
            { value: 'circle', label: 'Circle' },
            { value: 'square', label: 'Square' },
            { value: 'capsule', label: 'Capsule' },
          ].map((shape) => (
            <button
              key={shape.value}
              type="button"
              onClick={() => handleChange('shape', shape.value)}
              className={`
                px-3 py-2 rounded-lg text-xs font-medium transition-all
                ${
                  config.shape === shape.value
                    ? 'bg-gradient-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }
              `}
            >
              {shape.label}
            </button>
          ))}
        </div>
      </div>

      {/* Corner Radius Toggle */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Corner Radius
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.hasCornerRadius}
              onChange={(e) => handleChange('hasCornerRadius', e.target.checked)}
              className="mr-2"
            />
            <span className="text-xs text-gray-400">Enable</span>
          </label>
        </div>
        {config.hasCornerRadius && (
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              Radius: {config.cornerRadius}px
            </label>
            <input
              type="range"
              min="0"
              max="50"
              value={config.cornerRadius}
              onChange={(e) => handleChange('cornerRadius', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        )}
      </div>

      {/* Button Title (only for button type) */}
      {config.componentType === 'button' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button Title
          </label>
          <input
            type="text"
            value={config.buttonTitle}
            onChange={(e) => handleChange('buttonTitle', e.target.value)}
            className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border border-gray-600 focus:outline-none focus:border-primary-500"
            placeholder="Button"
          />
        </div>
      )}

      {/* Text Styling Section */}
      {config.componentType === 'button' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Text Color
            </label>
            <ColorPicker
              color={config.textColor}
              onChange={(color) => handleChange('textColor', color)}
              label="Text Color"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Font Size: {config.fontSize}pt
            </label>
            <input
              type="range"
              min="10"
              max="72"
              value={config.fontSize}
              onChange={(e) => handleChange('fontSize', parseInt(e.target.value))}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Font Weight
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {['regular', 'medium', 'semibold', 'bold'].map((weight) => (
                <button
                  key={weight}
                  onClick={() => handleChange('fontWeight', weight)}
                  className={`
                    px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                    ${
                      config.fontWeight === weight
                        ? 'bg-gradient-primary text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }
                  `}
                >
                  {weight.charAt(0).toUpperCase() + weight.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Font Name
            </label>
            <input
              type="text"
              value={config.fontName}
              onChange={(e) => handleChange('fontName', e.target.value)}
              className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border border-gray-600 focus:outline-none focus:border-primary-500"
              placeholder="system"
            />
            <p className="text-xs text-gray-400 mt-1">Use "system" for system font, or enter custom font name</p>
          </div>
        </>
      )}

      {/* Sizing Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Sizing Mode
        </label>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {['fixed', 'autolayout'].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => handleChange('sizingMode', mode)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${
                  config.sizingMode === mode
                    ? 'bg-gradient-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }
              `}
            >
              {mode === 'fixed' ? 'Fixed Size' : 'AutoLayout'}
            </button>
          ))}
        </div>

        {config.sizingMode === 'fixed' ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Width: {config.width}px
              </label>
              <input
                type="range"
                min="50"
                max="500"
                value={config.width}
                onChange={(e) => handleChange('width', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Height: {config.height}px
              </label>
              <input
                type="range"
                min="20"
                max="300"
                value={config.height}
                onChange={(e) => handleChange('height', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                AutoLayout Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'fullWidth', label: 'Full Width' },
                  { value: 'fullHeight', label: 'Full Height' },
                  { value: 'padding', label: 'Padding' },
                  { value: 'aspectRatio', label: 'Aspect Ratio' },
                ].map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleChange('autolayoutType', type.value)}
                    className={`
                      px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                      ${
                        config.autolayoutType === type.value
                          ? 'bg-gradient-primary text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {config.autolayoutType === 'padding' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Padding: {config.padding}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={config.padding}
                  onChange={(e) => handleChange('padding', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            )}

            {config.autolayoutType === 'aspectRatio' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Aspect Ratio: {config.aspectRatio}:1
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="3"
                  step="0.1"
                  value={config.aspectRatio}
                  onChange={(e) => handleChange('aspectRatio', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GradientControls;

